import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import ScrollReveal from "@/components/ScrollReveal";
import { CTA_DEVIS } from "@/data/forms";

const processSteps = [
  { num: "01", icon: Phone, title: "Premier contact", desc: "Vous nous appelez ou remplissez notre formulaire en ligne. Devis gratuit – réponse sous 48 h (jours ouvrables).", color: "#8B0000" },
  { num: "02", icon: "home", title: "Visite gratuite sur place", desc: "Nous nous déplaçons chez vous pour comprendre votre projet, prendre les mesures précises et discuter de vos attentes (60-90 minutes).", color: "#1a6b00" },
  { num: "03", icon: "file", title: "Devis détaillé sous 48 h", desc: "Vous recevez un devis détaillé poste par poste avec un planning prévisionnel précis. Toutes les conditions sont claires et transparentes.", color: "#B8860B" },
  { num: "04", icon: "pen", title: "Signature + acompte 30 %", desc: "Si le devis vous convient, signature du contrat avec acompte de 30 % pour démarrer les commandes de matériaux.", color: "#2563eb" },
  { num: "05", icon: "hammer", title: "Démarrage du chantier", desc: "Démarrage selon le planning convenu. Suivi quotidien par un chef de chantier dédié. Acomptes intermédiaires de 50 % selon avancement.", color: "#dc2626" },
  { num: "06", icon: "check", title: "Réception + garantie décennale", desc: "Réception contradictoire des travaux avec PV signé. Solde de 20 % à la livraison. Garantie décennale légale + 6 mois pour les vices cachés.", color: "#166534" },
];

export default function NotreProcessus() {
  return (
    <div data-testid="processus-page" style={{ backgroundColor: '#fff' }}>
      <SEOHead title="Comment ça marche ? Notre processus en 6 étapes | VGI Construct Bruxelles" description="Découvrez notre méthode de travail en 6 étapes : du premier contact à la réception avec garantie décennale. VGI Construct, votre entreprise générale de rénovation à Bruxelles et dans le Brabant." path="/notre-processus" />

      <section style={{ backgroundColor: '#0a0a0a', padding: '60px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <ScrollReveal>
            <p style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.2em', fontWeight: 700, color: '#8B0000', marginBottom: '12px' }}>Notre méthode</p>
            <h1 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 900, color: '#fff', marginBottom: '16px' }}>
              Comment ça <span style={{ color: '#8B0000' }}>marche ?</span>
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '17px', maxWidth: '500px', margin: '0 auto' }}>
              De votre premier appel à la remise des clés, voici nos 6 étapes
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section style={{ padding: '60px 0' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px', position: 'relative' }}>
          {/* Vertical timeline line */}
          <div style={{ position: 'absolute', left: '39px', top: '0', bottom: '0', width: '2px', backgroundColor: '#e8e8e8' }} className="max-sm:!left-[19px]" />

          {processSteps.map((s, i) => (
            <ScrollReveal key={s.num} delay={i * 100}>
              <div data-testid={`process-step-${s.num}`} style={{ display: 'flex', gap: '24px', marginBottom: '48px', position: 'relative' }}>
                {/* Timeline dot */}
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative', zIndex: 1, boxShadow: `0 0 0 6px #fff, 0 0 0 8px ${s.color}20` }} className="max-sm:!w-[28px] max-sm:!h-[28px]">
                  <span style={{ color: '#fff', fontSize: '14px', fontWeight: 900 }}>{s.num}</span>
                </div>
                {/* Content */}
                <div style={{ flex: 1, backgroundColor: '#fafafa', border: '1px solid #e8e8e8', borderRadius: '12px', padding: '24px', transition: 'all 0.3s' }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = s.color; e.currentTarget.style.boxShadow = `0 4px 16px ${s.color}15`; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor = '#e8e8e8'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <h3 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '20px', fontWeight: 800, color: '#1a1a1a', marginBottom: '8px' }}>{s.title}</h3>
                  <p style={{ fontSize: '15px', color: '#666', lineHeight: 1.7, margin: 0 }}>{s.desc}</p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: '#0a0a0a', padding: '56px 0' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <ScrollReveal>
            <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: 900, color: '#fff', marginBottom: '24px' }}>
              Démarrons votre <span style={{ color: '#8B0000' }}>projet !</span>
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px' }}>
              <Link to="/demande-devis" data-testid="processus-cta" className="btn-shimmer" style={{ backgroundColor: '#8B0000', color: '#fff', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '14px', padding: '16px 32px', textDecoration: 'none', borderRadius: '100px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                {CTA_DEVIS} <ArrowRight size={18} />
              </Link>
              <a href="tel:+32466407988" style={{ border: '2px solid rgba(255,255,255,0.25)', color: '#fff', fontWeight: 800, fontSize: '14px', padding: '14px 28px', textDecoration: 'none', borderRadius: '100px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <Phone size={18} /> +32 466 407 988
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
